/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/**
 * ChangeLog:
 * 20240412: DLD: Created initial override.
 * - Adding category image to the left of the category name when no sub-categories and products exist.
 * - Force title to h1
 */
const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    return {
        currentPage: getCurrentPage(state),
        totalItemCount: pagination?.totalItemCount,
    };
};

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

export interface CategoryNameStyles {
    wrapper: InjectableCss;
    heading?: TypographyPresentationProps;
    image?: LazyImageProps;
}

export const categoryNameStyles: CategoryNameStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            flex-wrap: wrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    heading: {
        css: css`
            font-size: 32px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    image: {
        width: "200px",
        height: "200px",
        css: css`
            border: 2px solid ${getColor("primary.main")};
            border-radius: 50%;
            margin-left: auto;
            padding: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0;
                        padding: 0;
                        border: none;
                    `,
                    css`
                        margin-left: 0;
                        padding: 0;
                        border: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = categoryNameStyles;

const CategoryName = ({ category, currentPage, totalItemCount }: Props) => {
    if (!category) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography
                variant="h1"
                data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
                {...styles.heading}
            >
                {category.shortDescription ?? category.name}
            </Typography>
            {totalItemCount && totalItemCount > 0 && (
                <LazyImage
                    src={category.largeImagePath}
                    altText={category.imageAltText}
                    {...styles.image}
                    data-test-selector="categoryImagePath"
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryName)),
    definition: {
        group: "Category Details",
        icon: "folder-bookmark",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
